import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

/**
 * SearchableSelect — a Select replacement that adds an inline search input
 * inside the dropdown, but ONLY when there are 10+ items. For smaller
 * lists it behaves identically to the existing shadcn `<Select>` so muscle
 * memory isn't broken on lists where typing-to-search would be silly.
 *
 * Why a custom component (instead of a shadcn Select wrapper):
 *   • Radix Select intentionally does NOT support an in-dropdown text input
 *     because it manages keyboard nav by typeahead-from-trigger. Wedging
 *     an Input inside breaks that nav. So we build a button + popover
 *     using simple Tailwind primitives that we already use elsewhere.
 *
 * Props:
 *   • value          — the selected option `value` (string)
 *   • onChange(v)    — fired with the new value (or "" for the cleared
 *                      "-- None --" option, when allowEmpty is true)
 *   • options        — `[{ value, label, hint? }, …]`
 *   • placeholder    — shown when nothing is selected
 *   • allowEmpty     — when true, the first row is "-- None --" / "Unassigned"
 *   • emptyLabel     — text for the allowEmpty row
 *   • searchThreshold — list size at which the search bar appears (default 10)
 *   • disabled
 *   • data-testid    — applied to the trigger button
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  allowEmpty = false,
  emptyLabel = "-- None --",
  searchThreshold = 10,
  disabled = false,
  className = "",
  triggerClassName = "",
  triggerIcon = null,
  "data-testid": testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // The list we render — empty row prepended when allowEmpty is true.
  const fullList = useMemo(() => {
    const rows = [...options];
    if (allowEmpty) {
      return [{ value: "", label: emptyLabel, _empty: true }, ...rows];
    }
    return rows;
  }, [options, allowEmpty, emptyLabel]);

  const showSearch = options.length >= searchThreshold;

  // Filter on substring (case-insensitive). Keep the empty row regardless.
  const filtered = useMemo(() => {
    if (!showSearch || !query.trim()) return fullList;
    const q = query.trim().toLowerCase();
    return fullList.filter(opt => {
      if (opt._empty) return true;
      const haystack = `${opt.label || ""} ${opt.hint || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [fullList, query, showSearch]);

  const selectedLabel = useMemo(() => {
    if (allowEmpty && (!value || value === "")) return placeholder;
    const found = options.find(o => o.value === value);
    return found?.label || placeholder;
  }, [value, options, allowEmpty, placeholder]);

  // Reset highlight when filter shrinks list
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered, highlight]);

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (open && showSearch) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    if (!open) setQuery("");
    return undefined;
  }, [open, showSearch]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const choose = (opt) => {
    onChange?.(opt._empty ? "" : opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(h => (h + 1) % Math.max(filtered.length, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => (h - 1 + filtered.length) % Math.max(filtered.length, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) choose(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const isSelected = (opt) => {
    if (opt._empty) return !value || value === "";
    return opt.value === value;
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background transition-colors hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${triggerClassName}`}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`flex items-center gap-1.5 truncate text-left ${
            (!value || value === "") && allowEmpty ? "text-muted-foreground" : "text-foreground"
          }`}
        >
          {triggerIcon}
          <span className="truncate">{selectedLabel}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ml-2 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[180px] rounded-md border border-border bg-popover text-popover-foreground shadow-2xl overflow-hidden"
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          {showSearch && (
            <div className="p-2 border-b border-border/50">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Search…"
                  className="w-full h-8 pl-8 pr-7 rounded-md bg-muted/40 text-xs text-foreground placeholder:text-muted-foreground border border-transparent focus:border-border focus:outline-none"
                  data-testid={testId ? `${testId}-search` : undefined}
                  aria-label="Search options"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div
            ref={listRef}
            role="listbox"
            className="max-h-[min(260px,45vh)] overflow-y-auto overscroll-contain touch-pan-y py-1"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid={testId ? `${testId}-empty` : undefined}>
                No matches.
              </div>
            ) : (
              filtered.map((opt, idx) => {
                const selected = isSelected(opt);
                const highlighted = idx === highlight;
                return (
                  <button
                    key={opt._empty ? "__empty__" : opt.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(opt)}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-2 ${
                      highlighted ? "bg-accent" : ""
                    } ${selected ? "font-medium" : "font-normal"}`}
                    data-testid={
                      testId
                        ? `${testId}-option-${opt._empty ? "none" : opt.value}`
                        : undefined
                    }
                  >
                    <span className={`truncate ${opt._empty ? "text-muted-foreground italic" : "text-foreground"}`}>
                      {opt.label}
                      {opt.hint && (
                        <span className="ml-1.5 text-muted-foreground text-[10px]">{opt.hint}</span>
                      )}
                    </span>
                    {selected && <Check className="w-3.5 h-3.5 text-foreground shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
